import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Copy, ExternalLink, Image, Video } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";

const VideosPage = () => {
  const [copied, setCopied] = useState<string | null>(null);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(text);
    toast.success("Link copiado para a área de transferência!");
    setTimeout(() => setCopied(null), 2000);
  };

  const videoTools = [
    {
      name: "OneImageVideo",
      description: "Transforme imagens estáticas em vídeos dinâmicos com IA",
      url: "https://www.oneimagevideo.com/",
      features: ["Crie vídeos a partir de uma única imagem", "Animações suaves e realistas", "Ideal para marketing e redes sociais"]
    },
    {
      name: "Vheer",
      description: "Plataforma de conversão de imagem para vídeo com tecnologia avançada",
      url: "https://vheer.com/app/image-to-video",
      features: ["Interface amigável e intuitiva", "Múltiplos estilos de animação", "Exportação em diversos formatos"]
    },
    {
      name: "Canva",
      description: "Ferramenta completa de design com recursos para criação de vídeos",
      url: "https://www.canva.com/",
      features: ["Modelos prontos para uso", "Editor completo de vídeos", "Recursos gratuitos e premium"]
    },
    {
      name: "Vídeos Engraçados",
      description: "Acesse nossa coleção de vídeos curtos e engraçados",
      url: "/videos-engracados",
      features: ["Vídeos curtos engraçados", "Transmissões ao vivo", "Tutoriais em vídeo"]
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="container mx-auto px-4 pt-24 pb-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent py-2">
            Ferramentas de Criação de Vídeo
          </h1>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            Conheça as melhores ferramentas online para transformar imagens em vídeos e criar conteúdo audiovisual de qualidade.
          </p>
        </div>

        <Tabs defaultValue="oneImageVideo" className="w-full mb-12">
          <div className="flex justify-center mb-8">
            <TabsList>
              <TabsTrigger value="oneImageVideo">OneImageVideo</TabsTrigger>
              <TabsTrigger value="vheer">Vheer</TabsTrigger>
              <TabsTrigger value="canva">Canva</TabsTrigger>
            </TabsList>
          </div>
          
          <TabsContent value="oneImageVideo" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">OneImageVideo</CardTitle>
                <CardDescription>Transforme uma imagem estática em um vídeo dinâmico e cativante</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-medium mb-2">Como funciona:</h3>
                    <ol className="list-decimal pl-5 space-y-2 text-muted-foreground">
                      <li>Faça upload de uma imagem de alta qualidade</li>
                      <li>Selecione o estilo de animação desejado</li>
                      <li>Ajuste as configurações de movimento e duração</li>
                      <li>Adicione música de fundo (opcional)</li>
                      <li>Exporte seu vídeo em alta qualidade</li>
                    </ol>
                  </div>
                  <div className="bg-muted p-4 rounded-lg">
                    <h3 className="text-lg font-medium mb-2">Vantagens:</h3>
                    <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                      <li>Tecnologia de IA avançada para movimentos naturais</li>
                      <li>Resultados profissionais sem conhecimento técnico</li>
                      <li>Ideal para marketing digital e redes sociais</li>
                      <li>Economize tempo na criação de conteúdo em vídeo</li>
                      <li>Excelente para dar vida a fotos de produtos</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button asChild className="bg-primary hover:bg-primary/90 text-primary-foreground">
                  <a 
                    href="https://www.oneimagevideo.com/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <Video className="mr-2 h-4 w-4" /> Acessar OneImageVideo
                  </a>
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => handleCopy("https://www.oneimagevideo.com/")}
                  className={copied === "https://www.oneimagevideo.com/" ? "bg-primary/10" : ""}
                >
                  <Copy size={16} className="mr-1" /> 
                  {copied === "https://www.oneimagevideo.com/" ? "Copiado!" : "Copiar Link"}
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
          
          <TabsContent value="vheer" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Vheer</CardTitle>
                <CardDescription>Plataforma de conversão de imagem para vídeo com tecnologia avançada</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-medium mb-2">Recursos principais:</h3>
                    <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                      <li>Interface intuitiva e amigável</li>
                      <li>Múltiplos estilos de animação pré-definidos</li>
                      <li>Controle preciso sobre o movimento e efeitos</li>
                      <li>Biblioteca de músicas e efeitos sonoros</li>
                      <li>Exportação em diversos formatos e resoluções</li>
                    </ul>
                  </div>
                  <div className="bg-muted p-4 rounded-lg">
                    <h3 className="text-lg font-medium mb-2">Casos de uso:</h3>
                    <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                      <li>Anúncios para redes sociais</li>
                      <li>Apresentações de produtos</li>
                      <li>Conteúdo para e-commerce</li>
                      <li>Animação de fotos pessoais</li>
                      <li>Marketing digital e publicidade</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button asChild className="bg-primary hover:bg-primary/90 text-primary-foreground">
                  <a 
                    href="https://vheer.com/app/image-to-video" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <Image className="mr-2 h-4 w-4" /> Acessar Vheer
                  </a>
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => handleCopy("https://vheer.com/app/image-to-video")}
                  className={copied === "https://vheer.com/app/image-to-video" ? "bg-primary/10" : ""}
                >
                  <Copy size={16} className="mr-1" /> 
                  {copied === "https://vheer.com/app/image-to-video" ? "Copiado!" : "Copiar Link"}
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
          
          <TabsContent value="canva" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Canva</CardTitle>
                <CardDescription>Plataforma completa de design com recursos para criação de vídeos</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-medium mb-2">Ferramentas de vídeo:</h3>
                    <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                      <li>Editor de vídeo completo e intuitivo</li>
                      <li>Milhares de modelos prontos para personalização</li>
                      <li>Biblioteca de elementos, animações e transições</li>
                      <li>Ferramentas de recorte, corte e edição</li>
                      <li>Recursos de texto animado e efeitos visuais</li>
                    </ul>
                  </div>
                  <div className="bg-muted p-4 rounded-lg">
                    <h3 className="text-lg font-medium mb-2">Por que usar o Canva:</h3>
                    <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                      <li>Solução completa para design gráfico e vídeo</li>
                      <li>Versão gratuita com muitos recursos</li>
                      <li>Ideal para iniciantes e profissionais</li>
                      <li>Colaboração em tempo real com equipes</li>
                      <li>Constantes atualizações e novos recursos</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button asChild className="bg-primary hover:bg-primary/90 text-primary-foreground">
                  <a 
                    href="https://www.canva.com/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="mr-2 h-4 w-4" /> Acessar Canva
                  </a>
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => handleCopy("https://www.canva.com/")}
                  className={copied === "https://www.canva.com/" ? "bg-primary/10" : ""}
                >
                  <Copy size={16} className="mr-1" /> 
                  {copied === "https://www.canva.com/" ? "Copiado!" : "Copiar Link"}
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {videoTools.map((tool, index) => (
            <Card key={index} className="flex flex-col h-full">
              <CardHeader>
                <CardTitle>{tool.name}</CardTitle>
                <CardDescription>{tool.description}</CardDescription>
              </CardHeader>
              <CardContent className="flex-grow">
                <h3 className="text-sm font-medium mb-2">Principais recursos:</h3>
                <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
                  {tool.features.map((feature, i) => (
                    <li key={i}>{feature}</li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter className="pt-2">
                <Button 
                  asChild 
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  <a 
                    href={tool.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    Acessar {tool.name}
                  </a>
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        <div className="bg-muted p-6 rounded-lg">
          <h2 className="text-2xl font-semibold mb-4">Dicas para Criação de Vídeos</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium mb-2">Para Iniciantes:</h3>
              <ol className="list-decimal pl-5 space-y-2 text-muted-foreground">
                <li>Comece com imagens de alta qualidade para melhores resultados</li>
                <li>Use modelos prontos para economizar tempo</li>
                <li>Mantenha vídeos curtos (15-30 segundos) para redes sociais</li>
                <li>Escolha músicas que complementem o tom do seu vídeo</li>
                <li>Experimente diferentes estilos de animação</li>
              </ol>
            </div>
            <div>
              <h3 className="text-lg font-medium mb-2">Para Resultados Profissionais:</h3>
              <ol className="list-decimal pl-5 space-y-2 text-muted-foreground">
                <li>Planeje seu storyboard antes de começar</li>
                <li>Use uma paleta de cores consistente</li>
                <li>Adicione chamadas para ação claras</li>
                <li>Otimize para diferentes plataformas (vertical/horizontal)</li>
                <li>Teste diferentes durações e transições</li>
              </ol>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default VideosPage;